import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Drawer,
  FormControlLabel,
  Typography,
  IconButton,
  Switch,
  Paper,
} from "@material-ui/core";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  FileTypeData,
  SourceTypeData,
  SearchConditionData,
} from "../../types/documents";
import { FileTypeToggleButton } from "../filetype/FileTypeToggleButton";
import { SourceTypeToggleButton } from "../sourcetype/SourceTypeToggleButton";
import { DateRangeSelect } from "../date/DateRangeSelect";
import { DateRangePicker } from "../date/DateRangePicker";
import {
  UserGroupIconChecboxGroup,
  UserGroupIconCheckboxDef,
} from "./UserGroupIconCheckboxGroup";
import { IconCheckboxGroupData } from "./IconChecboxGroup";
import { LayoutConst } from "../../constants";
import { isPropertyOfSourceTypeData } from "../../helpers/typeguard/sourcetype-typeguard";
import { isPropertyOfFileTypeData } from "../../helpers/typeguard/filetype-typeguard";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerPaper: {
      top: "24px",
      width: LayoutConst.searchConditionDrawer.width,
      padding: "0.25rem",
    },
    drawerHeader: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0, 1),
      // necessary for content to be below app bar
      // ...theme.mixins.toolbar,
      justifyContent: "flex-start",
      height: "32px",
    },
    formGroupHeader: {
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
    },
    userGroupPaper: {
      marginTop: "0.25rem",
      padding: "0.25rem",
    },
    userGroupContent: {
      paddingLeft: "1.25rem",
    },
    sourceTypePaper: {
      marginTop: "0.25rem",
      padding: "0.25rem",
    },
    fileTypePaper: {
      marginTop: "0.25rem",
      padding: "0.25rem",
    },
    dateRangePaper: {
      marginTop: "0.25rem",
      padding: "0.25rem",
    },
    dateRangeContent: {
      marginLeft: "1rem",
    },
  })
);

/** 検索条件ペイン用ユーザーグループ定義 */
export interface SearchUserGroupDefs {
  defs: UserGroupIconCheckboxDef[];
  data: IconCheckboxGroupData;
  enabled: boolean;
  update: boolean;
}

interface Props {
  open: boolean;
  userGroupDefs?: SearchUserGroupDefs | null;
  data: SearchConditionData;
  onChange: (newData: SearchConditionData) => void;
  onClose: () => void;
}

/** 検索条件ドロワーコンポーネント */
export function SearchConditionDrawer(props: Props) {
  const classes = useStyles();

  const { open, userGroupDefs, data, onChange, onClose } = props;

  //------------------------------
  // ユーザーグループ定義
  const [userGroupDefsState, setUserGroupDefsState] = React.useState<
    SearchUserGroupDefs | undefined | null
  >(userGroupDefs);

  // ユーザーグループデータ
  const [userGroupData, setUserGroupData] = React.useState<{
    [name: string]: boolean;
  }>(data.userGroupData);

  //------------------------------
  // 取得元
  const [sourceTypeData, setSourceTypeData] = React.useState<SourceTypeData>(
    data.sourceTypeData
  );

  //------------------------------
  // ファイルタイプ
  const [fileTypeData, setFileTypeData] = React.useState<FileTypeData>(
    data.fileTypeData
  );

  //------------------------------
  // 日付指定詳細モード
  const [dateDetailMode, setDateDetailMode] = React.useState<boolean>(
    data.dateTypeData.dateDetailMode
  );
  // 日付種類
  const [dateType, setDateType] = React.useState(data.dateTypeData.dateType);
  // 日付指定(簡易)
  const [dateValue, setDateValue] = React.useState(data.dateTypeData.dateValue);
  // 日付指定(詳細)
  const [selectedFromDate, setSelectedFromDate] = React.useState<Date | null>(
    data.dateTypeData.selectedFromDate
  );
  const [selectedToDate, setSelectedToDate] = React.useState<Date | null>(
    data.dateTypeData.selectedToDate
  );

  // 表示時から初期値が変わったら
  if (userGroupDefsState?.update === false && userGroupDefs?.update === true) {
    setUserGroupDefsState(userGroupDefs);
    setUserGroupData(userGroupDefs.data);
  }

  const fireConditionChanged = () => {
    const newData: SearchConditionData = {
      userGroupData,
      sourceTypeData,
      fileTypeData,
      dateTypeData: {
        dateDetailMode,
        dateType,
        dateValue,
        selectedFromDate,
        selectedToDate,
      },
    };
    onChange(newData);
  };

  // ユーザーグループ
  const handleUserGroupChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // チェックを変更する項目以外のチェック項目を取得
    const filter = Object.keys(userGroupData).filter(key => {
      return (key !== event.target.name && userGroupData[key]);
    });

    // チェックされた項目がなくなる場合、チェックは外さない。
    if (filter.length === 0 && !event.target.checked){
      return;
    }
    setUserGroupData({
      ...userGroupData,
      [event.target.name]: event.target.checked,
    });
  };

  // 取得元タイプ
  const handleSourceTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const name = event.currentTarget.getAttribute("name");

    // チェックを変更する項目以外のチェック項目を取得
    const filter = Object.keys(sourceTypeData).filter(key => {
      return (isPropertyOfSourceTypeData(key) && key !== name && sourceTypeData[key]);
    });

    if (isPropertyOfSourceTypeData(name)) {
      // チェックされた項目がなくなる場合、チェックは外さない。
      if (filter.length === 0 && sourceTypeData[name]){
        return;
      }
      setSourceTypeData({
        ...sourceTypeData,
        [name]: !sourceTypeData[name],
      });
    }
  };

  // ファイルタイプ
  const handleFileTypeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const name = event.currentTarget.getAttribute("name");

    // チェックを変更する項目以外のチェック項目を取得
    const filter = Object.keys(fileTypeData).filter(key => {
      return (isPropertyOfFileTypeData(key) && key !== name && fileTypeData[key]);
    });

    if (isPropertyOfFileTypeData(name)) {
      // チェックされた項目がなくなる場合、チェックは外さない。
      if (filter.length === 0 && fileTypeData[name]){
        return;
      }
      setFileTypeData({
        ...fileTypeData,
        [name]: !fileTypeData[name],
      });
    }
  };

  // 日付指定詳細モード
  const handleDateDetailModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDateDetailMode(event.target.checked);
  };

  // 日付種類
  const handleDateTypeChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setDateType(event.target.value as string);
  };

  // 日付指定(簡易)
  const handleDateValueChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setDateValue(event.target.value as string);
  };

  // [Effect]
  // 条件が変更されたらdataに反映する
  React.useEffect(() => {
    let cleanedUp = false;

    const fetchData = async () => {
      if (!cleanedUp) {
        fireConditionChanged();
      }
    };
    fetchData();

    return function cleanup() {
      cleanedUp = true;
    };
  }, [
    userGroupData,
    sourceTypeData,
    fileTypeData,
    dateDetailMode,
    dateType,
    dateValue,
    selectedFromDate,
    selectedToDate,
  ]);

  //--------------------
  // コンポーネント
  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      classes={{ paper: classes.drawerPaper }}
    >
      {/* Drawer ヘッダー */}
      <div className={classes.drawerHeader}>
        {/*
          ドロワーを閉じる
        */}
        <IconButton
          style={{ fontSize: "1rem" }}
          onClick={() => {
            onClose();
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </IconButton>
      </div>
      {userGroupDefsState?.enabled && (
        <Paper className={classes.userGroupPaper}>
          {/*
            ユーザーグループ
          */}
          <Typography component="div" className={classes.formGroupHeader}>
            ユーザーグループ
          </Typography>
          <div className={classes.userGroupContent}>
            <UserGroupIconChecboxGroup
              defs={userGroupDefsState?.defs}
              data={userGroupData}
              handleChange={handleUserGroupChange}
            />
          </div>
        </Paper>
      )}
      {/* [HP稼働]: electronからの修正(不要処理削除) */}
      {/* <Paper className={classes.sourceTypePaper}> */}
        {/*
          取得元
        */}
        {/* <Typography component="div" className={classes.formGroupHeader}>
          取得元
        </Typography>
        <SourceTypeToggleButton
          state={sourceTypeData}
          onChange={handleSourceTypeChange}
        />
      </Paper> */}
      <Paper className={classes.fileTypePaper}>
        {/*
          ファイル種類
        */}
        <Typography component="div" className={classes.formGroupHeader}>
          ファイル種類
        </Typography>
        <FileTypeToggleButton
          state={fileTypeData}
          handleClick={handleFileTypeClick}
        />
      </Paper>
      {/* [HP稼働]: electronからの修正(不要処理削除) */}
      {/* <Paper className={classes.dateRangePaper}> */}
        {/*
          日付範囲
        */}
        {/* <Typography component="div" className={classes.formGroupHeader}>
          日付範囲
        </Typography>
        <div className={classes.dateRangeContent}>
          <FormControlLabel
            control={(
              <Switch
                checked={dateDetailMode}
                onChange={handleDateDetailModeChange}
                color="primary"
                name="dateDetailMode"
                size="small"
              />
            )}
            label={<span style={{ fontSize: "0.9rem" }}>詳細に指定する</span>}
          />
          {!dateDetailMode ? (
            // 作成日付、更新日付 (簡易)
            <DateRangeSelect
              dateType={dateType}
              handleDateTypeChange={handleDateTypeChange}
              dateValue={dateValue}
              handleDateValueChange={handleDateValueChange}
            />
          ) : (
            // 作成日付、更新日付 (詳細)
            <DateRangePicker
              dateType={dateType}
              handleDateTypeChange={handleDateTypeChange}
              selectedFromDate={selectedFromDate}
              setSelectedFromDate={setSelectedFromDate}
              selectedToDate={selectedToDate}
              setSelectedToDate={setSelectedToDate}
            />
          )}
        </div>
      </Paper> */}
    </Drawer>
  );
}
